import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApplicationListRequestParams } from '@intellio/shared/models';
import { timer } from 'rxjs';
import { exhaustMap, map, takeWhile } from 'rxjs/operators';
import { BaseService } from './base-service.service';
import { AppConfigService } from './app-config.service';
import { AuthService } from './auth.service';

export interface BulkStatusChange {
  id: string;
  state: 'Preparing' | 'Ready' | 'Running' | 'Completed' | 'Failed';
  total: number;
  eligible: number;
  processed: number;
  updated: number;
  statusId?: string;
  statusName?: string;
  message?: string;
  businessIdLabel?: string;
}

export interface BulkStatusChangeItem {
  id: string;
  projectId: string;
  projectName: string;
  address: string;
  typeName: string;
  fromStatus: string;
  toStatus: string;
  outcome: string;
  message: string;
}

export interface BulkStatusChangePage {
  items: BulkStatusChangeItem[];
  next: string | null;
}

export type BulkStatusChangeResult = 'success' | 'failure';

export interface BulkStatusChangeOverrideRequired{
  requiresStatusBlockingTaskOverride: boolean;
  statusBlockingApplicationCount: number;
}

export type BulkStatusChangeConfirmResponse = 
  | BulkStatusChange
  | BulkStatusChangeOverrideRequired;

@Injectable({ providedIn: 'root' })
export class BulkStatusChangeService extends BaseService {
  private readonly path = '/api/applications/bulk-status-changes';

  constructor() {
    super(inject(HttpClient), inject(AppConfigService), inject(AuthService));
  }

  prepare(
    categoryId: string,
    applicationIds: string[],
    selectAll: boolean,
    params: ApplicationListRequestParams
  ) {
    const query = {
      ...params,
      alert: params.alert ? Number(params.alert) : null,
      actionRequiredAlertRule: Array.isArray(params.actionRequiredAlertRule)
        ? params.actionRequiredAlertRule
        : params.actionRequiredAlertRule
        ? [params.actionRequiredAlertRule]
        : [],
    };
    return this.post<BulkStatusChange>(this.path, {
      categoryId,
      applicationIds,
      selectAll,
      query,
    }).pipe(map((r) => r.data));
  }

  status(id: string) {
    return this.get<BulkStatusChange>(`${this.path}/${id}`).pipe(map((r) => r.data));
  }

  watch(id: string) {
    return timer(0, 1000).pipe(
      exhaustMap(() => this.get<BulkStatusChange>(`${this.path}/${id}`)),
      map((r) => r.data),
      takeWhile(
        (job) => job.state === 'Preparing' || job.state === 'Running',
        true
      )
    );
  }

  statuses(categoryId: string) {
    return this.get<{ id: string; name: string }[]>(`${this.path}/statuses`, {
      params: { categoryId },
    }).pipe(map((r) => r.data));
  }

  confirm(id: string, statusId: string, overrideStatusBlockingTasks = false) {
    return this.post<BulkStatusChangeConfirmResponse>(`${this.path}/${id}/confirm`, {
      statusId,overrideStatusBlockingTasks
    }).pipe(map((r) => r.data));
  }

  resume(id: string) {
    return this.post<BulkStatusChange>(`${this.path}/${id}/resume`).pipe(
      map((r) => r.data)
    );
  }

  items(id: string, after?: string, result?: BulkStatusChangeResult) {
    const params: Record<string, string> = {};
    if (after) params['after'] = after;
    if (result) params['result'] = result;
    return this.get<BulkStatusChangePage>(`${this.path}/${id}/items`, {
      params,
    }).pipe(map((r) => r.data));
  }
}

